import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  Heading,
  Image,
  Spacing,
  Stack,
  Text,
} from "@noom/wax-component-library";

import { EmployerDisambiguationInput } from "@/components/forms";
import { BaseOutline } from "@/components/survey/questionTypes/core";
import { useAppContext } from "@/contexts";
import { useEligibility, useHandleSponsorSelect, useTrackEvent } from "@/hooks";

const PAGE_ID = "eligibilityDisambiguation";

export const EligibilityDisambiguationView: React.FC = () => {
  const { candidateMatches } = useAppContext();
  const { redirectToCandidateMatch } = useEligibility();
  const navigate = useNavigate();
  const { handleSponsorSelect, isLoading } = useHandleSponsorSelect(
    candidateMatches,
    redirectToCandidateMatch,
  );
  const { trackAnonEvent } = useTrackEvent();
  const { t } = useTranslation();

  const hasCandidateMatches = candidateMatches.length > 0;

  useEffect(() => {
    if (!hasCandidateMatches) {
      return;
    }
    trackAnonEvent("b2bEnrollmentAnonPageViewed", { pageId: PAGE_ID });
  }, [hasCandidateMatches, trackAnonEvent]);

  // Candidate matches are transient in-memory state; on a refresh/deep-link
  // there is nothing to disambiguate, so send the user back to the start.
  if (!hasCandidateMatches) {
    return <Navigate replace to="/" />;
  }

  return (
    <BaseOutline>
      <Button
        aria-label="Back"
        onClick={() => navigate(-1)}
        variant="unstyled"
      >
        <Image
          alt="Back"
          cursor="pointer"
          margin="var(--spacing-m)"
          src="/assets/img/left-arrow.svg"
        />
      </Button>
      <Box
        display="flex"
        flexDirection="column"
        margin={{ base: "0 var(--spacing-l)", lg: "0 auto" }}
        maxWidth="400px"
        paddingTop="var(--spacing-xl)"
        width="100%"
      >
        <Stack spacing={Spacing[8]}>
          <Stack spacing={Spacing[4]}>
            <Heading>{t("eligibilityCheck.multipleMatches.title")}</Heading>
            <Text>{t("eligibilityCheck.multipleMatches.description")}</Text>
          </Stack>
          <EmployerDisambiguationInput
            isLoading={isLoading}
            onSelect={handleSponsorSelect}
          />
        </Stack>
      </Box>
    </BaseOutline>
  );
};
